import React, { useEffect, useState, useCallback, useRef } from "react";
import {
    IPoll,
    RootStateType,
    IChannel,
    ITextFieldCollection,
} from "@Interfaces";
import Grid from "@mui/material/Grid";
import { PollItem, TextFieldCollectionCard } from "@Components";
import { compose } from "redux";
import { withPublicLayout } from "@Hocs";
import { useLocation, useNavigate } from "react-router-dom";
import { PollActions } from "@Store";
import { useDispatch, useSelector } from "react-redux";
import { actions as dialogActions } from "@Store/Reducers/dialog";
import { DialogComponents } from "@Interfaces/Entities/Dialogs";
import { ChoiceHTMLSelector } from "@Interfaces/Entities/Poll";
import { Helmet } from "react-helmet";
import { isGuestDataStored } from "@Utils/isGuestDataStored";
import { getChannelCoverUrl } from "@Utils/getChannelCoverUrl";
import { getPollTextFields } from "@Services/API/PollTextField";
import { isDataCollectionRequired } from "@Utils/isDataCollectionRequired";

const useQuery = () => {
    const { search } = useLocation();
    return React.useMemo(() => new URLSearchParams(search), [search]);
};

const SinglePollPage: React.FC = () => {
    const dispatch = useDispatch<any>();
    const navigate = useNavigate();
    const location = useLocation();
    const query = useQuery();
    const hasProcessed = useRef(false);

    const [poll, setPoll] = useState<IPoll | null>(null);
    const [cid, setCid] = useState<string | null>(null);
    const [pid, setPid] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [textFields, setTextFields] = useState<ITextFieldCollection[]>([]);

    const currentPoll = useSelector(
        (state: RootStateType) => state.poll
    ) as IPoll | null;
    const channels = useSelector(
        (state: RootStateType) => state.channels.channels
    ) as IChannel[];

    // Get poll ID from query and fetch poll data
    useEffect(() => {
        const pollId = query.get("pid");
        if (pollId && pollId !== pid) {
            setPid(pollId);
            dispatch(PollActions.fetchSinglePoll(pollId));
        }
    }, [dispatch, query, pid]);

    // Update local state when Redux store updates
    useEffect(() => {
        if (currentPoll) {
            setPoll(currentPoll);
            setIsLoading(false);
        }
    }, [currentPoll]);

    const channelId = poll?.channel_id ?? null;

    useEffect(() => {
        if (!channelId) {
            setTextFields([]);
            return;
        }

        let canceled = false;
        const fetchTextFields = async () => {
            try {
                const { data } = await getPollTextFields(channelId);
                if (!canceled) {
                    setTextFields(data);
                }
            } catch (error) {
                if (!canceled) {
                    setTextFields([]);
                }
            }
        };

        fetchTextFields();

        return () => {
            canceled = true;
        };
    }, [channelId]);

    // Get choice ID (cid) from query
    useEffect(() => {
        const queryChoice = query.get("cid");
        if (queryChoice && queryChoice !== cid) {
            setCid(queryChoice);
        }
    }, [query, cid]);

    // Function to display vote confirmation dialog
    const findChoiceAndShowConfirmation = useCallback(
        (choiceId: string, pollId: string, allowComments: boolean) => {
            if (!pollId || !choiceId) return;

            const choiceTxtEl = document.getElementById(
                `${ChoiceHTMLSelector.txt}${choiceId}`
            );

            if (choiceTxtEl) {
                dispatch(
                    dialogActions.openDialog({
                        id: DialogComponents.VoteConfirmationDialog,
                        props: {
                            choice: choiceTxtEl.textContent,
                            choiceId,
                            pollId,
                            allowComments,
                        },
                    })
                );
            }
        },
        [dispatch]
    );

    // If a choice ID exists, show vote confirmation. Otherwise, collect guest data.
    useEffect(() => {
        if (hasProcessed.current) return; // Stops repeated executions

        if (pid && cid) {
            const allowComments = currentPoll?.settings?.allow_comments ?? true;
            setTimeout(() => {
                findChoiceAndShowConfirmation(cid, pid, allowComments);
            }, 300); // Delay to allow DOM rendering

            hasProcessed.current = true; // Mark as processed
        } else if (
            pid &&
            !isGuestDataStored() &&
            isDataCollectionRequired(currentPoll?.settings)
        ) {
            // Try to resolve the channel cover image from channels store (if available)
            const channel = channels?.find(
                (c) => c._id === currentPoll?.channel_id
            );
            const coverUrl = getChannelCoverUrl(channel);
            dispatch(
                dialogActions.openDialog({
                    id: DialogComponents.DataCollectionDialog,
                    props: {
                        settings: currentPoll?.settings,
                        author: currentPoll?.author,
                        coverUrl,
                    },
                })
            );
        }
    }, [
        pid,
        cid,
        dispatch,
        currentPoll,
        findChoiceAndShowConfirmation,
        channels,
    ]);

    // Redirect to home if no valid parameters are present
    useEffect(() => {
        if (!pid && !cid && !location.search) {
            navigate("/");
        }
    }, [cid, navigate, pid, location.search]);

    useEffect(() => {
        if (cid) {
            navigate("/single", { replace: true }); // Only remove `cid` from the URL after processing
        }
    }, [cid, navigate]);

    if (isLoading) return <div>Loading poll...</div>;

    return (
        <>
            {poll && (
                <>
                    {cid && (
                        <Helmet>
                            <meta
                                name="apple-mobile-web-app-capable"
                                content="yes"
                            />
                            <meta name="mobile-web-app-capable" content="yes" />
                        </Helmet>
                    )}
                    <Grid item xs={12}>
                        <PollItem
                            {...poll}
                            votingTimeLimit={
                                poll.settings?.voting_time_limit || 0
                            }
                            disabledShare={!!cid}
                            isHideResult={
                                poll.settings?.hide_results_before_voting ||
                                false
                            }
                        />
                    </Grid>
                    {textFields.map((textField) => (
                        <Grid item xs={12} key={textField._id}>
                            <TextFieldCollectionCard
                                textField={textField}
                                author={{
                                    username: poll.author.username,
                                    avatar: poll.author.avatar,
                                }}
                                published={poll.published}
                            />
                        </Grid>
                    ))}
                </>
            )}
        </>
    );
};

export default compose(withPublicLayout)(SinglePollPage);
